import React, {
  useMemo, useState, useCallback, FunctionComponent, useEffect,
} from 'react';
import { makeStyles } from '@material-ui/core';
import get from 'lodash/get';

import { ListItem } from '#web-components/types/table';
import Typography from '#web-components/components/Typography';
import { EntityGroup, EntityItem } from '#web-components/types/groupedEntity';
import ServiceCard from '#web-components/components/ServiceCard';

import clsx from 'clsx';
import EntityGroupComponent from './components/EntityGroup';
import styles from './GroupedEntityList.styles';

const useStyles = makeStyles(styles, { name: 'GroupedEntityList' });

export interface GroupedEntityListProps {
  groupList: Array<EntityGroup>,
  ungroupedList: Array<EntityItem>,
  onGroupSelect?: (item: EntityGroup | null) => void,
  onEntitySelect?: (item: ListItem) => void,
  readonly?: boolean,
  SelectedGroupActions?: FunctionComponent<{ item: EntityGroup, unselectGroup: () => void }>,
  EntityTableActions?: FunctionComponent<{
    item: ListItem,
    list: Array<EntityItem>,
    groupName?: string,
    existingGroupNames: string[],
    moveToUngroupedEnable?: boolean,
  }>,
  EntityGroupTableActions?: FunctionComponent<{ item: EntityGroup, list: Array<EntityGroup> }>,
  localization:{
    groupsTitle: string,
    ungroupsTitle: string,
    emptyGroupsPlaceholder: string,
    emptyCurrentGroupPlaceholder: string,
  },
  'data-xpath'?: {
    backToGroups?: string,
    groupsTitle?: string,
    ungroupedTitle?: string,
    groupItemTitle?: string,
    ungroupedItemTitle?: string,
  },
  existingGroupNames?: string[],
}

export default function GroupedEntityList(props: GroupedEntityListProps) {
  const {
    groupList,
    ungroupedList,
    onGroupSelect,
    onEntitySelect,
    localization,
    readonly,
    existingGroupNames,
    SelectedGroupActions,
    EntityTableActions,
    EntityGroupTableActions,
    'data-xpath': dataXpath,
  } = props;
  const classes = useStyles(props);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);

  const normalizedGroupsList = useMemo(() => {
    return (groupList.map((item) => ({ ...item, id: get(item, 'id', item.name) })) || []);
  }, [groupList]);

  const selectGroup = useCallback((index: number) => {
    setSelectedGroupIndex(index);
    if (onGroupSelect) {
      onGroupSelect(normalizedGroupsList[index]);
    }
  }, [normalizedGroupsList, onGroupSelect]);

  const backToGroups = useCallback(() => {
    setSelectedGroupIndex(null);
    if (onGroupSelect) {
      onGroupSelect(null);
    }
  }, [onGroupSelect]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [selectedGroupIndex]);

  if (selectedGroupIndex !== null) {
    return (
      <EntityGroupComponent
        backToGroups={backToGroups}
        group={normalizedGroupsList[selectedGroupIndex]}
        onEntitySelect={onEntitySelect}
        readonly={readonly}
        existingGroupNames={existingGroupNames}
        GroupActions={SelectedGroupActions}
        EntityTableActions={EntityTableActions}
        emptyPlaceholder={localization.emptyCurrentGroupPlaceholder}
        data-xpath={{ backToGroups: dataXpath?.backToGroups }}
        groupTitle={localization.ungroupsTitle}
      />
    );
  }

  return (
    <>
      {!!normalizedGroupsList.length && (
      <Typography data-xpath={dataXpath?.groupsTitle} className={classes.mb2} variant="textSmallCompact">
        {localization.groupsTitle}
      </Typography>
      )}
      {normalizedGroupsList.map((item) => (
        <ServiceCard
          data-xpath={dataXpath?.groupItemTitle}
          variant="default"
          className={classes.rowDistance}
          key={item.id}
          onClick={() => selectGroup(normalizedGroupsList.findIndex((group) => group.name === item.id))}
          actions={EntityGroupTableActions && !readonly
             && <EntityGroupTableActions item={item as EntityGroup} list={normalizedGroupsList} />}
        >
          {item.name}
        </ServiceCard>
      ))}
      {ungroupedList?.length ? (
        <>
          <Typography
            className={clsx(classes.mb2, classes.mt6)}
            variant="textSmallCompact"
            data-xpath={dataXpath?.ungroupedTitle}
          >{localization.ungroupsTitle}
          </Typography>
          {ungroupedList.map((item) => (
            <ServiceCard
              data-xpath={dataXpath?.ungroupedItemTitle}
              className={classes.rowDistance}
              variant="compact"
              key={item.id}
              onClick={() => onEntitySelect && onEntitySelect(item)}
              actions={EntityTableActions && !readonly
                  && (
                  <EntityTableActions
                    item={item}
                    list={ungroupedList}
                    existingGroupNames={existingGroupNames || []}
                  />
                  )}
            >
              {item.name}
            </ServiceCard>
          ))}
        </>
      ) : null}
    </>
  );
}
